import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { isChairmanReadOnly } from '../utils/access';

const API          = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/loans';
const MEMBERS_API  = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/members';
const TREASURY_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/treasury/accounts';

const LOAN_TYPES = [
  { value: 'normal',         label: 'Normal — secured by member shareholding' },
  { value: 'vip',            label: 'VIP — external security presented' },
  { value: 'rosca_payout',   label: 'ROSCA Payout Guarantee — secured against upcoming payout' },
  { value: 'credit_history', label: 'Credit History Backed — committee discretion' },
];
const LOAN_TYPE_COLORS = { normal: '#3498db', vip: '#f39c12', rosca_payout: '#9b59b6', credit_history: '#27ae60' };
const APP_LOAN_TYPE_LABELS = { member_loan: 'Standard Member Loan', emergency_loan: 'Emergency Loan', development_loan: 'Development Loan', rosca_advance: 'ROSCA Advance', security_backed_loan: 'Security-Backed Loan' };
const ACTIVE_STATUSES  = ['active', 'pending'];
const HISTORY_STATUSES = ['settled', 'defaulted', 'written_off'];

const fmtTS = (ts) => {
  if (!ts) return 'Pending';
  const d = new Date(ts);
  d.setMinutes(d.getMinutes() + d.getTimezoneOffset() + 180);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' EAT';
};

const addLetterhead = (doc, title, ref, date) => {
  doc.setFillColor(44, 62, 80); doc.rect(0, 0, 210, 28, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
  doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
  doc.setFontSize(11); doc.setFont('helvetica', 'normal');
  doc.text(title, 105, 21, { align: 'center' });
  doc.setTextColor(0, 0, 0); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(`Ref: ${ref}`, 15, 36); doc.text(`Date: ${date}`, 195, 36, { align: 'right' });
  doc.setDrawColor(44, 62, 80); doc.setLineWidth(0.5); doc.line(15, 39, 195, 39);
  return 45;
};
const addMemberBlock = (doc, y, data) => {
  doc.setFillColor(240, 240, 240); doc.rect(15, y, 180, 22, 'F');
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
  doc.text('MEMBER DETAILS', 18, y + 6);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  doc.text(`Name: ${data.member_name}`, 18, y + 13); doc.text(`Member No: ${data.member_number}`, 18, y + 19);
  doc.text(`Phone: ${data.phone || 'N/A'}`, 110, y + 13); doc.text(`Email: ${data.email || 'N/A'}`, 110, y + 19);
  return y + 28;
};
const addFooter = (doc) => {
  const ph = doc.internal.pageSize.height;
  doc.setFillColor(44, 62, 80); doc.rect(0, ph - 15, 210, 15, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text('Kigezi Association — Official Document — Confidential', 105, ph - 6, { align: 'center' });
};
const addSignatureBlock = (doc, y, labels, data, officerColumn = 0, borrowerInfo = null) => {
  const colWidth = 170 / labels.length;
  if (data.signature_snapshot) {
    try {
      const fmt = data.signature_snapshot.includes('data:image/png') ? 'PNG' : 'JPEG';
      const sigX = 15 + (officerColumn * colWidth);
      doc.setFillColor(255, 255, 255); doc.rect(sigX, y - 12, 42, 11, 'F');
      doc.addImage(data.signature_snapshot, fmt, sigX, y - 12, 40, 10);
    } catch (e) {}
  }
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  labels.forEach((label, i) => {
    const x = 15 + (i * colWidth);
    doc.setDrawColor(100, 100, 100); doc.line(x, y, x + colWidth - 5, y);
    doc.text(label, x, y + 5);
    if (i === officerColumn && data.authorised_by) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
      doc.text(data.officer_name || 'Finance Committee', x, y + 11);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
      doc.text(data.officer_title || 'Finance Chairperson', x, y + 16);
      doc.text(`Date: ${(data.disbursement_date || '').slice(0, 10)}`, x, y + 21);
      doc.setTextColor(0, 0, 0); doc.setFontSize(9);
    } else if (i === 0 && borrowerInfo) {
      if (borrowerInfo.signature) {
        try {
          const bFmt = borrowerInfo.signature.includes('data:image/png') ? 'PNG' : 'JPEG';
          doc.setFillColor(255, 255, 255); doc.rect(x, y - 12, 42, 11, 'F');
          doc.addImage(borrowerInfo.signature, bFmt, x, y - 12, 40, 10);
        } catch (e) {}
      }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
      doc.text(borrowerInfo.name, x, y + 11);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
      doc.text(`Member No: ${borrowerInfo.member_number}`, x, y + 16);
      doc.text(borrowerInfo.accepted_at ? `Accepted: ${fmtTS(borrowerInfo.accepted_at)}` : 'Acceptance: Pending', x, y + 21);
      doc.setTextColor(0, 0, 0); doc.setFontSize(9);
    } else { doc.text('Date: ___________', x, y + 11); }
  });
  return y + 28;
};
const addClause = (doc, y, text) => {
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  const lines = doc.splitTextToSize(text, 180);
  doc.text(lines, 15, y);
  return y + (lines.length * 5) + 3;
};

const generateLoanAgreement = (loan) => {
  const doc = new jsPDF();
  const disbDate = loan.disbursement_date?.slice(0, 10) || '';
  const genTS = fmtTS(loan.created_at);
  const accTS = loan.agreement_accepted_at ? fmtTS(loan.agreement_accepted_at) : 'Pending member acceptance via portal';
  let y = addLetterhead(doc, 'LOAN AGREEMENT', loan.loan_number, disbDate);
  y = addMemberBlock(doc, y, loan);
  autoTable(doc, {
    startY: y, head: [['Loan Terms', 'Details']],
    body: [
      ['Loan Number', loan.loan_number],
      ['Principal Amount (UGX)', parseFloat(loan.principal || 0).toLocaleString()],
      ['Administrative Fee (' + loan.admin_fee_rate + '%)', parseFloat(loan.admin_fee_amount || 0).toLocaleString()],
      ['Net Amount Disbursed (UGX)', parseFloat(loan.net_disbursed || 0).toLocaleString()],
      ['Disbursement Reference', loan.disbursement_ref || 'Pending — to be confirmed by Finance'],
      ['Loan Type', LOAN_TYPES.find(t => t.value === loan.loan_type)?.label || loan.loan_type],
      ['Interest Method', loan.repayment_method === 'flat' ? 'Flat Rate — interest on original principal' : 'Reducing Balance — interest on outstanding balance'],
      ['Repayment Term (Months)', loan.term_months],
      ['Monthly Dev Contribution Rate', loan.dev_contribution_rate + '%'],
      ['Monthly Instalment (UGX)', parseFloat(loan.monthly_instalment || 0).toLocaleString()],
      ['Disbursement Date', disbDate], ['Final Repayment Date', loan.due_date?.slice(0, 10) || ''],
      ['Security Details', loan.security_details || 'Member shareholding'], ['', ''],
      ['📄 Document Generated', genTS], ['✅ Member Agreement Accepted', accTS],
    ],
    styles: { fontSize: 9 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 90 } }, margin: { left: 15, right: 15 },
    didParseCell: (data) => { if (data.row.index >= 14 && data.section === 'body') { data.cell.styles.fillColor = [235, 245, 255]; data.cell.styles.textColor = [30, 60, 120]; data.cell.styles.fontStyle = 'bold'; } }
  });
  y = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
  doc.text('SCHEDULE A — TERMS AND CONDITIONS', 15, y); doc.setTextColor(0, 0, 0); y += 8;
  ['A1. The borrower agrees to repay the full principal plus all applicable charges within the agreed term.',
   'A2. A monthly development contribution applies to the outstanding principal for the duration of the loan.',
   'A3. Any missed repayment triggers an immediate Debit Note and attracts a monthly penalty on arrears.',
   "A4. The Association reserves the right to offset the borrower's ROSCA savings against outstanding loan balances.",
   'A5. Legal proceedings may be initiated by Executive Committee resolution where repayment fails after due notice.',
   'A6. Member acceptance via the KAMS portal constitutes a legally binding digital signature under this agreement.',
   'A7. This agreement is governed by the Constitution of Kigezi Association and the laws of Uganda.',
  ].forEach(t => { y = addClause(doc, y, t); });
  y += 4;
  if (y > 240) { doc.addPage(); y = 20; }
  y = addSignatureBlock(doc, y, ['Borrower Signature', 'Finance Chairperson', 'Association Chairperson'], loan, 1,
    { name: loan.member_name, member_number: loan.member_number, signature: loan.member_signature || null, accepted_at: loan.agreement_accepted_at || null });
  addFooter(doc);
  doc.save(`LoanAgreement-${loan.loan_number}-${disbDate}.pdf`);
};

const generatePaymentReceipt = (payment, loan) => {
  const doc = new jsPDF();
  const payDate = payment.payment_date?.slice(0, 10) || '';
  const ref = payment.receipt_ref || `RCP-${loan.loan_number}-${payDate}`;
  const data = { ...payment, member_name: loan.member_name, member_number: loan.member_number, phone: loan.phone, email: loan.email };
  let y = addLetterhead(doc, 'OFFICIAL RECEIPT — LOAN REPAYMENT', ref, payDate);
  y = addMemberBlock(doc, y, data);
  autoTable(doc, {
    startY: y, head: [['Field', 'Details']],
    body: [
      ['Loan Number', loan.loan_number], ['Payment Date', payDate],
      ['Total Amount Paid (UGX)', Number(payment.total_paid || 0).toLocaleString()],
      ['Principal Portion (UGX)', Number(payment.principal_portion || 0).toLocaleString()],
      ['Interest Portion (UGX)', Number(payment.interest_portion || 0).toLocaleString()],
      ['Principal Balance After (UGX)', Number(payment.principal_balance_after || 0).toLocaleString()],
      ['Interest Balance After (UGX)', Number(payment.interest_balance_after || 0).toLocaleString()],
      ['Receipt Reference', payment.receipt_ref || '—'],
      ['Notes', payment.notes || (payment.is_early_settlement ? '⭐ Early Settlement — Loan Fully Settled' : '—')],
    ],
    styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 10;
  const isSettled = payment.is_early_settlement || (parseFloat(payment.principal_balance_after) === 0 && parseFloat(payment.interest_balance_after) === 0);
  doc.setFillColor(isSettled ? 39 : 41, isSettled ? 174 : 128, isSettled ? 96 : 185);
  doc.rect(15, y, 180, 14, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
  doc.text(isSettled ? 'LOAN FULLY SETTLED — THANK YOU' : 'PAYMENT RECEIVED — THANK YOU', 105, y + 9, { align: 'center' });
  doc.setTextColor(0, 0, 0); y += 22;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('This receipt confirms the loan repayment recorded above. Please retain for your records.', 15, y, { maxWidth: 180 });
  y += 18;
  y = addSignatureBlock(doc, y, ['Received By (Finance Committee)', 'Position'], data, 0);
  addFooter(doc);
  doc.save(`Receipt-${loan.loan_number}-${payDate}.pdf`);
};

const generateLoanStatement = (loan, payments, interest_postings) => {
  const doc = new jsPDF();
  const today = new Date().toLocaleDateString('en-GB');
  let y = addLetterhead(doc, 'LOAN ACCOUNT STATEMENT', loan.loan_number, today);
  y = addMemberBlock(doc, y, loan);

  doc.setFillColor(234, 244, 251); doc.rect(15, y, 180, 28, 'F');
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
  doc.text('LOAN SUMMARY', 18, y + 6);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  doc.text(`Principal: UGX ${Number(loan.principal || 0).toLocaleString()}`, 18, y + 13);
  doc.text(`Net Disbursed: UGX ${Number(loan.net_disbursed || 0).toLocaleString()}`, 18, y + 19);
  doc.text(`Dev Rate: ${loan.dev_contribution_rate}%/month`, 18, y + 25);
  doc.text(`Disbursement Date: ${loan.disbursement_date?.slice(0, 10) || ''}`, 100, y + 13);
  doc.text(`Due Date: ${loan.due_date?.slice(0, 10) || ''}`, 100, y + 19);
  doc.text(`Status: ${(loan.status || '').toUpperCase()}`, 100, y + 25);
  y += 34;

  if (interest_postings && interest_postings.length > 0) {
    doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
    doc.text('INTEREST POSTINGS', 15, y); y += 4;
    autoTable(doc, {
      startY: y,
      head: [['Month', 'Date', 'Opening Balance', 'Rate', 'Interest Amount', 'Status']],
      body: interest_postings.map(p => [
        p.month_number,
        new Date(p.posting_date).toLocaleDateString('en-GB'),
        'UGX ' + Number(p.opening_balance || 0).toLocaleString(),
        p.rate + '%',
        'UGX ' + Number(p.interest_amount || 0).toLocaleString(),
        (p.status || '').toUpperCase(),
      ]),
      styles: { fontSize: 8 }, headStyles: { fillColor: [180, 119, 13] },
      margin: { left: 15, right: 15 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
  doc.text('REPAYMENT HISTORY', 15, y); y += 4;
  autoTable(doc, {
    startY: y,
    head: [['Date', 'Total Paid', 'Principal', 'Interest', 'Prin. Bal After', 'Int. Bal After', 'Note']],
    body: payments.map(p => [
      new Date(p.payment_date).toLocaleDateString('en-GB'),
      'UGX ' + Number(p.total_paid || 0).toLocaleString(),
      'UGX ' + Number(p.principal_portion || 0).toLocaleString(),
      'UGX ' + Number(p.interest_portion || 0).toLocaleString(),
      'UGX ' + Number(p.principal_balance_after || 0).toLocaleString(),
      'UGX ' + Number(p.interest_balance_after || 0).toLocaleString(),
      p.is_early_settlement ? 'Early Settlement' : (p.notes || ''),
    ]),
    styles: { fontSize: 8 }, headStyles: { fillColor: [26, 82, 118] },
    margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 8;

  const totalPaid     = payments.reduce((s, p) => s + parseFloat(p.total_paid || 0), 0);
  const totalPrincipal = payments.reduce((s, p) => s + parseFloat(p.principal_portion || 0), 0);
  const totalInterest  = payments.reduce((s, p) => s + parseFloat(p.interest_portion || 0), 0);
  doc.setFillColor(44, 62, 80); doc.rect(15, y, 180, 18, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(9); doc.setFont('helvetica', 'bold');
  doc.text(`Total Paid: UGX ${totalPaid.toLocaleString()}`, 20, y + 7);
  doc.text(`Principal: UGX ${totalPrincipal.toLocaleString()}`, 80, y + 7);
  doc.text(`Interest: UGX ${totalInterest.toLocaleString()}`, 140, y + 7);
  doc.text(`Outstanding Balance: UGX ${Number((parseFloat(loan.balance || 0) + parseFloat(loan.interest_balance || 0))).toLocaleString()}`, 20, y + 14);
  doc.setTextColor(0, 0, 0); y += 26;

  addFooter(doc);
  doc.save(`LoanStatement-${loan.loan_number}-${today.replace(/\//g, '-')}.pdf`);
};

function LoanCard({ loan, isHistory, onDetail, onPayment, onSchedule, onEdit, onDelete, disbRefId, disbRefVal, setDisbRefId, setDisbRefVal, saveDisbRef, openDisb, disbForm, setDisbForm, onRevert, fmt, smBtn, lbl, inlineInp, editingId, editData, setEditData, saveEdit, cancelEdit, isAdmin }) {
  const readOnly = isChairmanReadOnly();
  const statusColor = (s) => ({ active: '#1a5276', settled: '#1e8449', defaulted: '#c0392b', pending: '#e67e22', written_off: '#888' }[s] || '#888');
  if (editingId === loan.id) return (
    <div style={{ background: '#fffde7', border: '2px solid #f39c12', borderRadius: '8px', padding: '16px 20px', marginBottom: '12px' }}>
      <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '12px' }}>Editing: {loan.loan_number} — {loan.member_name}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ minWidth: '200px' }}><div style={lbl}>Loan Type</div><select style={inlineInp} value={editData.loan_type} onChange={e => setEditData({ ...editData, loan_type: e.target.value })}>{LOAN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
        <div style={{ minWidth: '140px' }}><div style={lbl}>Principal (UGX)</div><input type="number" style={inlineInp} value={editData.principal} onChange={e => setEditData({ ...editData, principal: e.target.value })} /></div>
        <div style={{ minWidth: '120px' }}><div style={lbl}>Admin Fee %</div><input type="number" style={inlineInp} value={editData.admin_fee_rate} onChange={e => setEditData({ ...editData, admin_fee_rate: e.target.value })} /></div>
        <div style={{ minWidth: '120px' }}><div style={lbl}>Dev Rate %</div><input type="number" style={inlineInp} value={editData.dev_contribution_rate} onChange={e => setEditData({ ...editData, dev_contribution_rate: e.target.value })} /></div>
        <div style={{ minWidth: '120px' }}><div style={lbl}>Method</div><select style={inlineInp} value={editData.repayment_method} onChange={e => setEditData({ ...editData, repayment_method: e.target.value })}><option value="flat">Flat Rate</option><option value="reducing">Reducing Balance</option></select></div>
        <div style={{ minWidth: '80px' }}><div style={lbl}>Term (months)</div><input type="number" style={inlineInp} value={editData.term_months} onChange={e => setEditData({ ...editData, term_months: e.target.value })} /></div>
        <div style={{ minWidth: '130px' }}><div style={lbl}>Disbursement Date</div><input type="date" style={inlineInp} value={editData.disbursement_date} onChange={e => setEditData({ ...editData, disbursement_date: e.target.value })} /></div>
        <div style={{ minWidth: '130px' }}><div style={lbl}>Due Date</div><input type="date" style={inlineInp} value={editData.due_date} onChange={e => setEditData({ ...editData, due_date: e.target.value })} /></div>
        <div style={{ minWidth: '100px' }}><div style={lbl}>Status</div><select style={inlineInp} value={editData.status} onChange={e => setEditData({ ...editData, status: e.target.value })}><option value="active">Active</option><option value="pending">Pending</option><option value="settled">Settled</option><option value="defaulted">Defaulted</option><option value="written_off">Written Off</option></select></div>
        <div style={{ minWidth: '180px' }}><div style={lbl}>Security Details</div><input style={inlineInp} value={editData.security_details} onChange={e => setEditData({ ...editData, security_details: e.target.value })} /></div>
      </div>
      <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
        {!readOnly && <button style={smBtn('#2ecc71')} onClick={() => saveEdit(loan.id)}>Save</button>}
        <button style={smBtn('#95a5a6')} onClick={cancelEdit}>Cancel</button>
      </div>
    </div>
  );
  return (
    <div style={{ background: isHistory ? '#f8f9fa' : 'white', border: `1px solid ${isHistory ? '#dee2e6' : '#e0e0e0'}`, borderRadius: '8px', padding: '16px 20px', marginBottom: '12px', opacity: isHistory ? 0.9 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <span style={{ fontWeight: '700', fontSize: '15px', color: '#2c3e50' }}>{loan.loan_number}</span>
            <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: statusColor(loan.status), color: 'white' }}>{loan.status.toUpperCase()}</span>
            <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '11px', background: LOAN_TYPE_COLORS[loan.loan_type] || '#888', color: 'white', fontWeight: '600' }}>{LOAN_TYPES.find(t => t.value === loan.loan_type)?.label.split('—')[0].trim() || loan.loan_type.toUpperCase()}</span>
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>{loan.member_name} — {loan.member_number}</div>
          <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>
            Disbursed: {new Date(loan.disbursement_date).toLocaleDateString('en-GB')} | Due: {new Date(loan.due_date).toLocaleDateString('en-GB')} | {loan.term_months} months
            {isHistory && loan.settlement_date && <span style={{ color: '#1e8449', marginLeft: '8px', fontWeight: '600' }}>✓ Settled: {new Date(loan.settlement_date).toLocaleDateString('en-GB')}</span>}
          </div>
          {!isHistory && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: loan.agreement_accepted_at ? '#d5f5e3' : '#fef9e7', color: loan.agreement_accepted_at ? '#1e8449' : '#b7770d' }}>{loan.agreement_accepted_at ? `✅ Agreement accepted ${new Date(loan.agreement_accepted_at).toLocaleDateString('en-GB')}` : '⏳ Awaiting agreement acceptance'}</span>
              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: loan.disbursement_ref ? '#d5f5e3' : '#fde8e8', color: loan.disbursement_ref ? '#1e8449' : '#c0392b' }}>{loan.disbursement_ref ? `💳 Ref: ${loan.disbursement_ref}` : '⚠ No disbursement ref'}</span>
              {loan.repayment_method === 'flat' && (
                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: '#eaf4fb', color: '#1a5276', fontWeight: 'bold' }}>💰 Single lump-sum repayment — due {new Date(loan.due_date).toLocaleDateString('en-GB')}</span>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center', padding: '8px 14px', background: isHistory ? '#e8f5e9' : '#eaf4fb', borderRadius: '6px', minWidth: '120px' }}><div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Principal Balance</div><div style={{ fontSize: '15px', fontWeight: '700', color: isHistory ? '#1e8449' : '#1a5276' }}>{fmt(loan.balance)}</div></div>
          <div style={{ textAlign: 'center', padding: '8px 14px', background: '#fef9e7', borderRadius: '6px', minWidth: '120px' }}><div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Interest Balance</div><div style={{ fontSize: '15px', fontWeight: '700', color: '#b7770d' }}>{fmt(loan.interest_balance)}</div></div>
          <div style={{ textAlign: 'center', padding: '8px 14px', background: '#f4ecf7', borderRadius: '6px', minWidth: '120px' }}><div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Total Outstanding</div><div style={{ fontSize: '15px', fontWeight: '700', color: '#6e2fa0' }}>{fmt(loan.total_outstanding)}</div></div>
        </div>
      </div>
      <div style={{ marginTop: '10px', display: 'flex', gap: '20px', fontSize: '12px', color: '#888', flexWrap: 'wrap' }}>
        <span>Principal paid: <strong style={{ color: '#1e8449' }}>{fmt(loan.principal_paid)}</strong></span>
        <span>Interest paid: <strong style={{ color: '#1e8449' }}>{fmt(loan.interest_paid)}</strong></span>
        <span>Admin fee: <strong>{fmt(loan.admin_fee_amount)}</strong> ({loan.admin_fee_rate}%)</span>
        <span>Dev rate: <strong>{loan.dev_contribution_rate}%/month</strong></span>
      </div>
      {!isHistory && loan.live_interest && !loan.live_interest.is_term_complete && (
        <div style={{ marginTop: '10px', background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '6px', padding: '10px 14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#1a5276', marginBottom: '6px' }}>💰 Live Balance (today)</div>
          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '12px' }}>
            <span>Principal remaining: <strong style={{ color: '#1a5276' }}>{fmt(loan.live_interest.principal_remaining)}</strong></span>
            <span>Interest owed: <strong style={{ color: '#b7770d' }}>{fmt(Math.round(loan.live_interest.interest_owed_now))}</strong></span>
            <span>Total if settled today: <strong style={{ color: '#c0392b' }}>{fmt(Math.round(loan.live_interest.total_owed_now))}</strong></span>
          </div>
          {loan.live_interest.current_month && (
            <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '6px' }}>
              Month {loan.live_interest.current_month.month_number}: {fmt(Math.round(loan.live_interest.current_month.daily_rate))}/day &middot; day {loan.live_interest.current_month.days_elapsed}/30 &middot; {fmt(Math.round(loan.live_interest.current_month.accrued_so_far))} accrued this month
            </div>
          )}
        </div>
      )}
      {!readOnly && !isHistory && !loan.disbursement_ref && (loan.status === 'active' || loan.status === 'pending') && (
        <div style={{ marginTop: '10px' }}>
          {disbRefId === loan.id ? (() => {
            const f = disbForm;
            const fi = { padding: '6px 8px', border: '1px solid #e74c3c', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' };
            const fl = { fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '3px' };
            const set = (k, v) => setDisbForm({ ...f, [k]: v });
            const pr = parseFloat(f.principal) || 0;
            const fee = pr * (parseFloat(f.admin_fee_rate) || 0) / 100;
            return (
              <div style={{ background: '#fff8f6', border: '1px solid #f5b7b1', borderRadius: '6px', padding: '12px' }}>
                <div style={{ fontWeight: 'bold', color: '#c0392b', marginBottom: '8px', fontSize: '13px' }}>Disburse {loan.loan_number} — check the details, then save</div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <div><div style={fl}>Amount (UGX)</div><input type="number" style={{ ...fi, width: '140px' }} value={f.principal} onChange={e => set('principal', e.target.value)} /></div>
                  <div><div style={fl}>Term (months)</div><input type="number" style={{ ...fi, width: '90px' }} value={f.term_months} onChange={e => set('term_months', e.target.value)} /></div>
                  <div><div style={fl}>Admin Fee %</div><input type="number" style={{ ...fi, width: '90px' }} value={f.admin_fee_rate} onChange={e => set('admin_fee_rate', e.target.value)} /></div>
                  <div><div style={fl}>Dev Rate %</div><input type="number" style={{ ...fi, width: '90px' }} value={f.dev_contribution_rate} onChange={e => set('dev_contribution_rate', e.target.value)} /></div>
                  <div><div style={fl}>Method</div><select style={fi} value={f.repayment_method} onChange={e => set('repayment_method', e.target.value)}><option value="flat">Flat Rate</option><option value="reducing">Reducing Balance</option></select></div>
                  <div><div style={fl}>Disbursement Date</div><input type="date" style={fi} value={f.disbursement_date} onChange={e => set('disbursement_date', e.target.value)} /></div>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div><div style={fl}>Paid From Account *</div>
                    <select style={{ ...fi, width: '170px' }} value={f.mode_of_payment} onChange={e => set('mode_of_payment', e.target.value)}>
                      <option value="">-- Select Account --</option>
                      <option value="cash">Cash</option>
                      <option value="centenary">Centenary Bank</option>
                      <option value="dfcu">DFCU Bank</option>
                    </select></div>
                  <div><div style={fl}>Disbursement Reference *</div><input style={{ ...fi, width: '240px' }} placeholder="Enter disbursement reference" value={disbRefVal} onChange={e => setDisbRefVal(e.target.value)} /></div>
                  <div style={{ fontSize: '12px', color: '#555' }}>Admin fee: <strong>{fmt(fee)}</strong><br />Net paid out: <strong style={{ color: '#c0392b' }}>{fmt(pr - fee)}</strong></div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button onClick={() => saveDisbRef(loan.id)} style={smBtn('#e74c3c')}>Save &amp; Disburse</button>
                  <button onClick={() => { setDisbRefId(null); setDisbRefVal(''); }} style={smBtn('#95a5a6')}>Cancel</button>
                </div>
              </div>
            );
          })() : (
            <button onClick={() => openDisb(loan)} style={{ ...smBtn('#e74c3c'), fontSize: '11px' }}>⚠ Add Disbursement Reference</button>
          )}
        </div>
      )}
      <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => generateLoanAgreement(loan)} style={{ background: '#1a5276', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>📄 Agreement</button>
        <button style={smBtn('#1a5276')} onClick={() => onDetail(loan)}>View Detail</button>
        <button style={smBtn('#555')} onClick={() => onSchedule(loan)}>Schedule</button>
        {!readOnly && !isHistory && loan.status === 'active' && <>
          <button style={smBtn('#1e8449')} onClick={() => onPayment(loan)}>Record Payment</button>
        </>}
        {!readOnly && !isHistory && <button style={smBtn('#e67e22')} onClick={() => onEdit(loan)}>Edit</button>}
        {!readOnly && !isHistory && isAdmin && loan.status === 'active' && loan.disbursement_ref && <button style={smBtn('#8e44ad')} onClick={() => onRevert(loan)}>↩ Revert to Pending</button>}
        {!readOnly && !isHistory && isAdmin && <button style={smBtn('#c0392b')} onClick={() => onDelete(loan)}>🗑 Delete</button>}
      </div>
    </div>
  );
}

function Loans() {
  const [loans, setLoans]               = useState([]);
  const [members, setMembers]           = useState([]);
  const [accounts, setAccounts]         = useState([]);
  const [applications, setApplications] = useState([]);
  const [view, setView]                 = useState('list');
  const [activeTab, setActiveTab]       = useState('loans');
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loanDetail, setLoanDetail]     = useState(null);
  const [schedule, setSchedule]         = useState(null);
  const [loading, setLoading]           = useState(false);
  const [msg, setMsg]                   = useState('');
  const [editingId, setEditingId]       = useState(null);
  const [editData, setEditData]         = useState({});
  const [rejectId, setRejectId]         = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveId, setApproveId]       = useState(null);
  const [approveData, setApproveData]   = useState({ approved_amount: '', admin_notes: '', term_months: '12', admin_fee_rate: '3', dev_contribution_rate: '3', repayment_method: 'flat', disbursement_date: '' });
  const [disbRefId, setDisbRefId]       = useState(null);
  const [disbRefVal, setDisbRefVal]     = useState('');
  const [disbForm, setDisbForm]         = useState({ principal: '', term_months: '', admin_fee_rate: '0', dev_contribution_rate: '', repayment_method: 'flat', disbursement_date: '', mode_of_payment: '' });

  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editPayData, setEditPayData]           = useState({});

  const [editingPostingId, setEditingPostingId] = useState(null);
  const [editPostingAmount, setEditPostingAmount] = useState('');

  const [form, setForm]       = useState({ member_id: '', loan_type: 'normal', principal: '', admin_fee_rate: '3', dev_contribution_rate: '3', repayment_method: 'flat', term_months: '', disbursement_date: '', security_details: '', security_description: '', mode_of_payment: '', disbursement_ref: '' });
  const [payForm, setPayForm] = useState({ total_paid: '', payment_date: '', receipt_ref: '', account_id: '', notes: '' });
  const readOnly = isChairmanReadOnly();

  const token    = localStorage.getItem('token');
  const headers  = { Authorization: `Bearer ${token}` };
  const userRole = token ? (() => { try { return JSON.parse(atob(token.split('.')[1])).role; } catch { return ''; } })() : '';
  const isAdmin  = userRole === 'admin';

  const fetchLoans        = () => { axios.get(API, { headers }).then(r => setLoans(r.data)).catch(() => {}); };
  const fetchApplications = () => { axios.get(`${API}/applications`, { headers }).then(r => setApplications(r.data)).catch(() => {}); };
  const reloadDetail      = (loanId) => { axios.get(`${API}/${loanId}`, { headers }).then(r => setLoanDetail(r.data)).catch(() => {}); };

  useEffect(() => {
    fetchLoans(); fetchApplications();
    axios.get(MEMBERS_API, { headers }).then(r => setMembers(r.data)).catch(() => {});
    axios.get(TREASURY_API, { headers }).then(r => setAccounts(r.data)).catch(() => {});
  }, []);

  const activeLoans  = loans.filter(l => ACTIVE_STATUSES.includes(l.status));
  const historyLoans = loans.filter(l => HISTORY_STATUSES.includes(l.status));
  const pendingCount = applications.filter(a => a.status === 'pending').length;

  const openDetail = (loan) => {
    setSelectedLoan(loan); setLoading(true);
    axios.get(`${API}/${loan.id}`, { headers }).then(r => { setLoanDetail(r.data); setLoading(false); setView('detail'); });
  };
  const openSchedule = (loan) => {
    setSelectedLoan(loan); setLoading(true);
    axios.get(`${API}/${loan.id}/schedule`, { headers }).then(r => { setSchedule(r.data); setLoading(false); setView('schedule'); });
  };
  const submitLoan = () => {
    if (!form.member_id || !form.principal || !form.term_months || !form.disbursement_date) return setMsg('Fill in all required fields');
    axios.post(API, form, { headers })
      .then(res => {
        setMsg('Loan disbursed successfully'); fetchLoans();
        setForm({ member_id: '', loan_type: 'normal', principal: '', admin_fee_rate: '3', dev_contribution_rate: '3', repayment_method: 'flat', term_months: '', disbursement_date: '', security_details: '', security_description: '', mode_of_payment: '', disbursement_ref: '' });
        setSelectedLoan(res.data); setLoading(true);
        axios.get(`${API}/${res.data.id}/schedule`, { headers }).then(r => { setSchedule(r.data); setLoading(false); setView('schedule'); });
      })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };
  const submitPayment = () => {
    if (!payForm.total_paid || !payForm.payment_date) return setMsg('Fill payment amount and date');
    axios.post(`${API}/${selectedLoan.id}/payment`, payForm, { headers })
      .then(r => {
        setMsg(`Payment recorded. Principal: UGX ${Number(r.data.principal_portion).toLocaleString()} | Interest: UGX ${Number(r.data.interest_portion).toLocaleString()}${r.data.is_early_settlement ? ' — LOAN FULLY SETTLED' : ''}`);
        fetchLoans(); setView('list');
      })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };

  const deleteLoan = (loan) => {
    if (!window.confirm(`Delete loan ${loan.loan_number} for ${loan.member_name}?\n\nThis is permanent. Any matching treasury disbursement/repayment transactions will be automatically reversed.`)) return;
    axios.delete(`${API}/${loan.id}`, { headers })
      .then(r => {
        setMsg(r.data.message + (r.data.reversed_disbursement ? ` — treasury disbursement of UGX ${Number(r.data.reversed_disbursement.amount).toLocaleString()} reversed` : ' — no treasury reversal needed'));
        fetchLoans();
      })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };

  const saveEditPayment = (paymentId) => {
    axios.patch(`${API}/payments/${paymentId}`, editPayData, { headers })
      .then(r => {
        setMsg('Payment updated — balances recalculated');
        setEditingPaymentId(null);
        reloadDetail(selectedLoan.id);
        fetchLoans();
      })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };

  const deletePayment = (paymentId, amount) => {
    if (!window.confirm(`Delete payment of UGX ${Number(amount).toLocaleString()}?\n\nThis will reverse its effect on loan balances. Treasury transaction must be adjusted manually.`)) return;
    axios.delete(`${API}/payments/${paymentId}`, { headers })
      .then(r => {
        setMsg('Payment deleted — ' + r.data.message);
        reloadDetail(selectedLoan.id);
        fetchLoans();
      })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };

  const saveEditPosting = (postingId) => {
    if (!editPostingAmount) return setMsg('Error: Enter new interest amount');
    axios.patch(`${API}/interest-postings/${postingId}`, { interest_amount: parseFloat(editPostingAmount) }, { headers })
      .then(r => {
        setMsg(r.data.message);
        setEditingPostingId(null);
        reloadDetail(selectedLoan.id);
        fetchLoans();
      })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };

  const reversePosting = (postingId, amount) => {
    if (!window.confirm(`Reverse interest posting of UGX ${Number(amount).toLocaleString()}?\n\nThis will remove the posting and reduce the loan interest balance accordingly.`)) return;
    axios.delete(`${API}/interest-postings/${postingId}`, { headers })
      .then(r => {
        setMsg(r.data.message);
        reloadDetail(selectedLoan.id);
        fetchLoans();
      })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };

  const approveApplication = (id) => {
    if (!approveData.approved_amount || parseFloat(approveData.approved_amount) <= 0) return setMsg('Error: Enter the approved amount');
    if (!approveData.disbursement_date) return setMsg('Error: Enter the disbursement date');
    axios.patch(`${API}/applications/${id}`, { status: 'approved', approved_amount: parseFloat(approveData.approved_amount), admin_notes: approveData.admin_notes, term_months: parseInt(approveData.term_months), admin_fee_rate: parseFloat(approveData.admin_fee_rate), dev_contribution_rate: parseFloat(approveData.dev_contribution_rate), repayment_method: approveData.repayment_method, disbursement_date: approveData.disbursement_date }, { headers })
      .then(r => { setMsg(`Application approved — Loan ${r.data.loan_id ? 'created automatically' : 'pending'}`); setApproveId(null); setApproveData({ approved_amount: '', admin_notes: '', term_months: '12', admin_fee_rate: '3', dev_contribution_rate: '3', repayment_method: 'flat', disbursement_date: '' }); fetchApplications(); fetchLoans(); })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };
  const rejectApplication = (id) => {
    if (!rejectReason.trim()) return setMsg('Enter a rejection reason');
    axios.patch(`${API}/applications/${id}`, { status: 'rejected', rejection_reason: rejectReason }, { headers })
      .then(() => { setMsg('Application rejected'); setRejectId(null); setRejectReason(''); fetchApplications(); })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };
  const openDisb = (loan) => {
    setDisbRefId(loan.id); setDisbRefVal('');
    setDisbForm({
      principal: String(parseFloat(loan.principal)), term_months: String(loan.term_months),
      admin_fee_rate: String(parseFloat(loan.admin_fee_rate || 0)), dev_contribution_rate: String(parseFloat(loan.dev_contribution_rate || 0)),
      repayment_method: loan.repayment_method || 'flat',
      disbursement_date: loan.disbursement_date ? loan.disbursement_date.slice(0, 10) : new Date().toISOString().split('T')[0],
      mode_of_payment: ''
    });
  };
  const saveDisbRef = (loanId) => {
    if (!disbRefVal.trim()) return setMsg('Error: Enter a disbursement reference');
    if (!disbForm.mode_of_payment) return setMsg('Error: Choose the account the loan was paid from');
    if (!window.confirm(`Disburse ${fmt((parseFloat(disbForm.principal) || 0) * (1 - (parseFloat(disbForm.admin_fee_rate) || 0) / 100))} from ${disbForm.mode_of_payment.toUpperCase()}?\n\nTreasury will be debited and the loan becomes Active.`)) return;
    axios.patch(`${API}/${loanId}/disbursement-ref`, {
      disbursement_ref: disbRefVal,
      mode_of_payment: disbForm.mode_of_payment,
      principal: parseFloat(disbForm.principal),
      term_months: parseInt(disbForm.term_months),
      admin_fee_rate: parseFloat(disbForm.admin_fee_rate),
      dev_contribution_rate: parseFloat(disbForm.dev_contribution_rate),
      repayment_method: disbForm.repayment_method,
      disbursement_date: disbForm.disbursement_date
    }, { headers })
      .then(r => { setMsg(`Loan disbursed — Treasury debited ${fmt(r.data.treasury_debited)}`); setDisbRefId(null); setDisbRefVal(''); fetchLoans(); })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };
  const revertLoan = (loan) => {
    if (!window.confirm(`Revert ${loan.loan_number} (${loan.member_name}) to Pending?\n\nThe disbursement reference is cleared and any Treasury debit for this loan is reversed.`)) return;
    axios.post(`${API}/${loan.id}/revert-to-pending`, {}, { headers })
      .then(r => { setMsg(`${loan.loan_number} reverted to Pending` + (r.data.reversed_debit ? ` — Treasury debit of ${fmt(r.data.reversed_debit)} reversed` : ' — no Treasury debit existed')); fetchLoans(); })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };
  const startEdit = (loan) => {
    setEditingId(loan.id);
    setEditData({ loan_type: loan.loan_type, principal: loan.principal, admin_fee_rate: loan.admin_fee_rate, dev_contribution_rate: loan.dev_contribution_rate, repayment_method: loan.repayment_method, term_months: loan.term_months, disbursement_date: loan.disbursement_date ? loan.disbursement_date.slice(0, 10) : '', due_date: loan.due_date ? loan.due_date.slice(0, 10) : '', status: loan.status, security_details: loan.security_details || '', security_description: loan.security_description || '' });
  };
  const cancelEdit = () => { setEditingId(null); setEditData({}); };
  const saveEdit = (id) => {
    axios.patch(`${API}/${id}`, editData, { headers })
      .then(() => { setMsg('Loan updated successfully'); setEditingId(null); fetchLoans(); })
      .catch(e => setMsg('Error: ' + (e.response?.data?.error || e.message)));
  };

  const printSchedule = () => {
    if (!schedule) return;
    const doc = new jsPDF();
    const f = (n) => 'UGX ' + Number(n || 0).toLocaleString();
    const isLumpSum = schedule.is_lump_sum || schedule.repayment_method === 'flat';

    doc.setFillColor(44, 62, 80); doc.rect(0, 0, 210, 28, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
    doc.setFontSize(11); doc.setFont('helvetica', 'normal');
    doc.text(isLumpSum ? 'Loan Repayment Statement (Flat Rate — Single Payment)' : 'Loan Repayment Schedule', 105, 20, { align: 'center' });
    doc.setTextColor(0, 0, 0); doc.setFontSize(10);
    doc.text(`Loan Number: ${schedule.loan_number}`, 14, 40);
    doc.text(`Member: ${schedule.member_name} (${schedule.member_number})`, 14, 47);
    doc.text(`Loan Type: ${LOAN_TYPES.find(t => t.value === schedule.loan_type)?.label || schedule.loan_type}`, 14, 54);
    doc.text(`Principal: ${f(schedule.principal)}`, 14, 61);
    doc.text(`Admin Fee Rate: ${schedule.admin_fee_rate}%`, 110, 40);
    doc.text(`Dev Contribution Rate: ${schedule.dev_contribution_rate}%/month`, 110, 47);
    doc.text(`Method: ${isLumpSum ? 'Flat Rate' : 'Reducing Balance'}`, 110, 54);
    doc.text(`Disbursed: ${schedule.disbursement_date ? schedule.disbursement_date.slice(0, 10) : ''}`, 110, 61);
    doc.text(`Due Date: ${schedule.due_date ? schedule.due_date.slice(0, 10) : ''}`, 110, 68);

    if (isLumpSum) {
      const row = schedule.schedule[0] || {};
      const principalPaid = parseFloat(schedule.principal_paid || 0);
      const interestPaid = parseFloat(schedule.interest_paid || 0);
      const hasPayments = principalPaid > 0 || interestPaid > 0;
      const remainingPrincipal = Math.max(0, parseFloat(row.principal_due || 0) - principalPaid);
      const remainingInterest = Math.max(0, parseFloat(row.interest_due || 0) - interestPaid);
      const remainingTotal = remainingPrincipal + remainingInterest;

      let y = 82;
      doc.setFillColor(234, 244, 251); doc.rect(14, y, 182, 30, 'F');
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
      doc.text('SINGLE LUMP-SUM REPAYMENT — NO MONTHLY INSTALMENTS', 105, y + 9, { align: 'center' });
      doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0); doc.setFontSize(9.5);
      doc.text(`Principal: ${f(row.principal_due)}`, 24, y + 20);
      doc.text(`Due Date: ${row.due_date || ''}`, 110, y + 20);
      y += 40;

      if (hasPayments) {
        doc.setFillColor(213, 245, 227); doc.rect(14, y, 182, 24, 'F');
        doc.setFontSize(9.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 132, 73);
        doc.text('PAYMENTS ALREADY MADE', 20, y + 8);
        doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
        doc.text(`Principal Paid: ${f(principalPaid)}`, 20, y + 17);
        doc.text(`Interest Paid: ${f(interestPaid)}`, 110, y + 17);
        y += 32;
      }

      doc.setDrawColor(192, 57, 43); doc.setLineWidth(0.8); doc.rect(14, y, 182, 34);
      doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(100, 100, 100);
      doc.text('CURRENTLY OWED (as of today)', 20, y + 8);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      doc.text(`Remaining Principal: ${f(remainingPrincipal)}`, 20, y + 17);
      doc.text(`Remaining Interest: ${f(remainingInterest)}`, 110, y + 17);
      doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(192, 57, 43);
      doc.text(`Total Remaining: ${f(remainingTotal)}`, 20, y + 28);
      y += 42;

      doc.setFontSize(8.5); doc.setFont('helvetica', 'italic'); doc.setTextColor(120);
      doc.text('One lump-sum payment covers the full remaining balance above, due on the date shown.', 14, y);
      doc.text('Interest is recalculated on the reduced principal after each payment.', 14, y + 6);
      y += 16;
      doc.setFontSize(8);
      doc.text('Early settlement closes the loan and stops all future interest charges.', 14, y);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, 14, y + 5);
    } else {
      const tP = schedule.schedule.reduce((s, r) => s + r.principal_due, 0);
      const tI = schedule.schedule.reduce((s, r) => s + r.interest_due, 0);
      const tD = schedule.schedule.reduce((s, r) => s + r.total_due, 0);
      autoTable(doc, {
        startY: 76,
        head: [['Month', 'Due Date', 'Opening Balance', 'Principal', 'Interest', 'Total Due', 'Closing Balance']],
        body: [...schedule.schedule.map(r => [r.month, r.due_date, f(r.opening_balance), f(r.principal_due), f(r.interest_due), f(r.total_due), f(r.closing_balance)]), ['', 'TOTALS', '', f(tP), f(tI), f(tD), '']],
        styles: { fontSize: 9, cellPadding: 3 }, headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        didParseCell: (data) => { if (data.row.index === schedule.schedule.length) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.fillColor = [230, 230, 230]; } },
      });
      const finalY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(8); doc.setTextColor(120);
      doc.text('Early settlement closes the loan and stops all future interest charges.', 14, finalY);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, 14, finalY + 5);
    }
    doc.save(`${schedule.loan_number}_${isLumpSum ? 'Statement' : 'Schedule'}.pdf`);
  };

  const fmt       = n => 'UGX ' + Number(n || 0).toLocaleString();
  const inp       = { width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' };
  const inlineInp = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #f39c12', fontSize: '12px', background: '#fffde7', width: '100%' };
  const lbl       = { display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px', color: '#2c3e50' };
  const fld       = { marginBottom: '14px' };
  const btn       = (bg) => ({ background: bg || '#2c3e50', color: 'white', border: 'none', padding: '9px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' });
  const smBtn     = (bg) => ({ background: bg || '#2c3e50', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
  const sharedCardProps = { fmt, smBtn, lbl, inlineInp, editingId, editData, setEditData, saveEdit, cancelEdit, disbRefId, disbRefVal, setDisbRefId, setDisbRefVal, saveDisbRef, openDisb, disbForm, setDisbForm, onRevert: revertLoan, isAdmin };

  const projectedInterest = () => {
    if (!form.principal || !form.dev_contribution_rate || !form.term_months) return 0;
    const p = parseFloat(form.principal), r = parseFloat(form.dev_contribution_rate), t = parseInt(form.term_months);
    if (form.repayment_method === 'flat') return (p * r / 100) * t;
    let bal = p, mp = p / t, total = 0;
    for (let i = 0; i < t; i++) { total += (bal * r) / 100; bal -= mp; }
    return total;
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#2c3e50' }}>Loans</h2>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: '13px' }}>Principal and interest tracked separately — early settlement supported</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {view !== 'list' && <button style={btn('#666')} onClick={() => { setView('list'); setMsg(''); setEditingPaymentId(null); }}>← Back</button>}
          {!readOnly && view === 'list' && <button style={btn()} onClick={() => setView('new')}>+ Disburse Loan</button>}
        </div>
      </div>

      {msg && (
        <div style={{ padding: '10px 16px', background: msg.startsWith('Error') ? '#fde8e8' : '#d5f5e3', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: msg.startsWith('Error') ? '#c0392b' : '#1e8449' }}>
          {msg} <span onClick={() => setMsg('')} style={{ float: 'right', cursor: 'pointer' }}>✕</span>
        </div>
      )}

      {view === 'list' && (
        <div>
          <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #ecf0f1' }}>
            <button onClick={() => setActiveTab('loans')} style={{ padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '13px', background: activeTab === 'loans' ? '#1a5276' : '#ecf0f1', color: activeTab === 'loans' ? 'white' : '#2c3e50', borderRadius: '4px 4px 0 0' }}>Active Loans ({activeLoans.length})</button>
            <button onClick={() => setActiveTab('history')} style={{ padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '13px', background: activeTab === 'history' ? '#1e8449' : '#ecf0f1', color: activeTab === 'history' ? 'white' : '#2c3e50', borderRadius: '4px 4px 0 0' }}>Loan History ({historyLoans.length})</button>
            <button onClick={() => setActiveTab('applications')} style={{ padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '13px', background: activeTab === 'applications' ? '#2c3e50' : '#ecf0f1', color: activeTab === 'applications' ? 'white' : '#2c3e50', borderRadius: '4px 4px 0 0' }}>
              Loan Applications ({applications.length})
              {pendingCount > 0 && <span style={{ background: '#e74c3c', color: 'white', borderRadius: '10px', fontSize: '10px', padding: '1px 6px', marginLeft: '6px', fontWeight: 'bold' }}>{pendingCount} pending</span>}
            </button>
          </div>

          {activeTab === 'loans' && (
            activeLoans.length === 0
              ? <div style={{ textAlign: 'center', padding: '60px', color: '#aaa' }}>No active loans</div>
              : <>
                  {activeLoans.map(loan => (
                    <LoanCard key={loan.id} loan={loan} isHistory={false}
                      onDetail={openDetail}
                      onPayment={(l) => { setSelectedLoan(l); setPayForm({ total_paid: '', payment_date: '', receipt_ref: '', account_id: '', notes: '' }); setView('payment'); }}
                      onSchedule={openSchedule} onEdit={startEdit} onDelete={deleteLoan}
                      {...sharedCardProps} />
                  ))}
                </>
          )}

          {activeTab === 'history' && (
            historyLoans.length === 0
              ? <div style={{ textAlign: 'center', padding: '60px', color: '#aaa' }}>No completed loans yet</div>
              : <>
                  <div style={{ background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', fontSize: '13px', color: '#1e8449' }}>
                    {historyLoans.length} completed loan{historyLoans.length !== 1 ? 's' : ''} — total principal disbursed: <strong>UGX {historyLoans.reduce((s, l) => s + parseFloat(l.principal || 0), 0).toLocaleString()}</strong>
                  </div>
                  {historyLoans.map(loan => (
                    <LoanCard key={loan.id} loan={loan} isHistory={true}
                      onDetail={openDetail} onSchedule={openSchedule}
                      onPayment={() => {}} onEdit={() => {}} onDelete={() => {}}
                      {...sharedCardProps} />
                  ))}
                </>
          )}

          {activeTab === 'applications' && (
            <div>
              {applications.length === 0
                ? <div style={{ textAlign: 'center', padding: '60px', color: '#aaa' }}>No loan applications yet</div>
                : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#2c3e50', color: 'white' }}>
                        {['Date', 'Member', 'Loan Type', 'Amount Requested', 'Purpose', 'Status', 'Decision', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map((a, i) => (
                        <>
                          <tr key={a.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white', borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '10px 12px' }}>{a.applied_at?.slice(0, 10)}</td>
                            <td style={{ padding: '10px 12px' }}><strong>{a.member_name}</strong><br /><span style={{ fontSize: '11px', color: '#7f8c8d' }}>{a.member_number}</span></td>
                            <td style={{ padding: '10px 12px' }}>{APP_LOAN_TYPE_LABELS[a.loan_type] || a.loan_type}</td>
                            <td style={{ padding: '10px 12px', fontWeight: 'bold', color: '#2c3e50' }}>UGX {parseFloat(a.amount_requested).toLocaleString()}</td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', maxWidth: '180px' }}>{a.purpose || '—'}</td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{ background: a.status === 'approved' ? '#2ecc71' : a.status === 'rejected' ? '#e74c3c' : '#f39c12', color: 'white', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>{a.status.toUpperCase()}</span>
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', color: '#7f8c8d' }}>
                              {a.reviewed_by_name && <div style={{ fontWeight: 'bold', color: '#2c3e50' }}>{a.reviewed_by_name}</div>}
                              {a.reviewed_at && <div style={{ fontSize: '10px' }}>{a.reviewed_at?.slice(0, 10)}</div>}
                              {a.approved_amount && <div style={{ color: '#27ae60', fontWeight: 'bold' }}>✅ UGX {parseFloat(a.approved_amount).toLocaleString()}</div>}
                              {a.loan_id && <div style={{ color: '#1a5276', fontSize: '11px' }}>Loan auto-created</div>}
                              {a.admin_notes && <div style={{ color: '#2980b9', fontSize: '11px' }}>{a.admin_notes}</div>}
                              {a.rejection_reason && <div style={{ color: '#e74c3c', fontSize: '11px' }}>Reason: {a.rejection_reason}</div>}
                              {!a.reviewed_by_name && '—'}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              {!readOnly && a.status === 'pending' && (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button onClick={() => { setApproveId(approveId === a.id ? null : a.id); setApproveData({ approved_amount: a.amount_requested, admin_notes: '', term_months: '12', admin_fee_rate: '0', dev_contribution_rate: '3', repayment_method: 'flat', disbursement_date: new Date().toISOString().split('T')[0] }); setRejectId(null); }} style={smBtn('#27ae60')}>✓ Approve</button>
                                  <button onClick={() => { setRejectId(rejectId === a.id ? null : a.id); setRejectReason(''); setApproveId(null); }} style={smBtn('#e74c3c')}>✕ Reject</button>
                                </div>
                              )}
                            </td>
                          </tr>
                          {!readOnly && approveId === a.id && a.status === 'pending' && (
                            <tr key={`approve-${a.id}`} style={{ background: '#f0fff4', borderBottom: '1px solid #a9dfbf' }}>
                              <td colSpan="8" style={{ padding: '16px 18px' }}>
                                <div style={{ fontWeight: 'bold', color: '#27ae60', marginBottom: '12px' }}>✓ Approve — {a.member_name}</div>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                  <div><div style={{ fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Approved Amount *</div><input type="number" style={{ padding: '7px', border: '1px solid #27ae60', borderRadius: '4px', fontSize: '13px', width: '180px' }} value={approveData.approved_amount} onChange={e => setApproveData({ ...approveData, approved_amount: e.target.value })} /></div>
                                  <div><div style={{ fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Term (Months) *</div><input type="number" style={{ padding: '7px', border: '1px solid #27ae60', borderRadius: '4px', fontSize: '13px', width: '100px' }} value={approveData.term_months} onChange={e => setApproveData({ ...approveData, term_months: e.target.value })} /></div>
                                  <div><div style={{ fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Admin Fee %</div><input type="number" style={{ padding: '7px', border: '1px solid #27ae60', borderRadius: '4px', fontSize: '13px', width: '90px' }} value={approveData.admin_fee_rate} onChange={e => setApproveData({ ...approveData, admin_fee_rate: e.target.value })} /></div>
                                  <div><div style={{ fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Dev Rate %</div><input type="number" style={{ padding: '7px', border: '1px solid #27ae60', borderRadius: '4px', fontSize: '13px', width: '90px' }} value={approveData.dev_contribution_rate} onChange={e => setApproveData({ ...approveData, dev_contribution_rate: e.target.value })} /></div>
                                  <div><div style={{ fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Method</div><select style={{ padding: '7px', border: '1px solid #27ae60', borderRadius: '4px', fontSize: '13px' }} value={approveData.repayment_method} onChange={e => setApproveData({ ...approveData, repayment_method: e.target.value })}><option value="flat">Flat Rate</option><option value="reducing">Reducing Balance</option></select></div>
                                  <div><div style={{ fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Disbursement Date *</div><input type="date" style={{ padding: '7px', border: '1px solid #27ae60', borderRadius: '4px', fontSize: '13px' }} value={approveData.disbursement_date} onChange={e => setApproveData({ ...approveData, disbursement_date: e.target.value })} /></div>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                  <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Notes (optional)</div>
                                  <input style={{ padding: '7px', border: '1px solid #27ae60', borderRadius: '4px', fontSize: '13px', width: '420px', boxSizing: 'border-box' }} value={approveData.admin_notes} onChange={e => setApproveData({ ...approveData, admin_notes: e.target.value })} />
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button onClick={() => approveApplication(a.id)} style={smBtn('#27ae60')}>✓ Confirm Approval</button>
                                  <button onClick={() => setApproveId(null)} style={smBtn('#95a5a6')}>Cancel</button>
                                </div>
                              </td>
                            </tr>
                          )}
                          {!readOnly && rejectId === a.id && a.status === 'pending' && (
                            <tr key={`reject-${a.id}`} style={{ background: '#fff5f5', borderBottom: '1px solid #f5c6cb' }}>
                              <td colSpan="8" style={{ padding: '12px 16px' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                  <input style={{ flex: 1, padding: '8px', border: '1px solid #e74c3c', borderRadius: '4px', fontSize: '13px' }} placeholder="Rejection reason (required)" value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
                                  <button onClick={() => rejectApplication(a.id)} style={smBtn('#e74c3c')}>Confirm Reject</button>
                                  <button onClick={() => { setRejectId(null); setRejectReason(''); }} style={smBtn('#95a5a6')}>Cancel</button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </div>
          )}
        </div>
      )}

      {view === 'new' && (
        <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '24px', maxWidth: '620px' }}>
          <h3 style={{ marginTop: 0, color: '#2c3e50' }}>Disburse New Loan</h3>
          <div style={fld}><label style={lbl}>Member *</label><select style={inp} value={form.member_id} onChange={e => setForm({ ...form, member_id: e.target.value })}><option value=''>-- Select Member --</option>{members.map(m => <option key={m.id} value={m.id}>{m.full_name} — {m.member_number}</option>)}</select></div>
          <div style={fld}><label style={lbl}>Loan Type *</label><select style={inp} value={form.loan_type} onChange={e => setForm({ ...form, loan_type: e.target.value })}>{LOAN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
          <div style={fld}><label style={lbl}>Principal Amount (UGX) *</label><input type='number' style={inp} value={form.principal} onChange={e => setForm({ ...form, principal: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...fld, flex: 1 }}><label style={lbl}>Admin Fee Rate (%) *</label><input type='number' style={inp} value={form.admin_fee_rate} onChange={e => setForm({ ...form, admin_fee_rate: e.target.value })} /></div>
            <div style={{ ...fld, flex: 1 }}><label style={lbl}>Monthly Dev Contribution (%) *</label><input type='number' style={inp} value={form.dev_contribution_rate} onChange={e => setForm({ ...form, dev_contribution_rate: e.target.value })} /></div>
          </div>
          <div style={fld}><label style={lbl}>Interest Method *</label><select style={inp} value={form.repayment_method} onChange={e => setForm({ ...form, repayment_method: e.target.value })}><option value='flat'>Flat Rate — single lump-sum repayment at term end</option><option value='reducing'>Reducing Balance — monthly instalments, interest on outstanding balance</option></select></div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...fld, flex: 1 }}><label style={lbl}>Term (Months) *</label><input type='number' style={inp} value={form.term_months} onChange={e => setForm({ ...form, term_months: e.target.value })} /></div>
            <div style={{ ...fld, flex: 1 }}><label style={lbl}>Disbursement Date *</label><input type='date' style={inp} value={form.disbursement_date} onChange={e => setForm({ ...form, disbursement_date: e.target.value })} /></div>
          </div>
          {form.principal && form.admin_fee_rate && form.dev_contribution_rate && form.term_months && (
            <div style={{ background: '#eaf4fb', borderRadius: '6px', padding: '14px', marginBottom: '16px', fontSize: '13px' }}>
              <strong style={{ color: '#1a5276' }}>Projection</strong>
              <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <span>Admin Fee: <strong>{fmt((parseFloat(form.principal) * parseFloat(form.admin_fee_rate)) / 100)}</strong></span>
                <span>Net Disbursed: <strong>{fmt(parseFloat(form.principal) - (parseFloat(form.principal) * parseFloat(form.admin_fee_rate)) / 100)}</strong></span>
                <span>Total Interest: <strong>{fmt(projectedInterest())}</strong></span>
                <span>Total Payable: <strong>{fmt(parseFloat(form.principal || 0) + projectedInterest())}</strong></span>
              </div>
              {form.repayment_method === 'flat' && (
                <div style={{ marginTop: '8px', color: '#1a5276', fontSize: '12px' }}>
                  This entire Total Payable amount is due in a single payment at the end of the {form.term_months}-month term — there are no monthly instalments.
                </div>
              )}
            </div>
          )}
          {(form.loan_type === 'normal' || form.loan_type === 'vip') && <div style={fld}><label style={lbl}>Security Details {form.loan_type === 'vip' ? '*' : '(optional)'}</label><input style={inp} value={form.security_details} onChange={e => setForm({ ...form, security_details: e.target.value })} placeholder={form.loan_type === 'vip' ? 'e.g. Land Title LRV 1234' : 'Member shares'} /></div>}
          {form.loan_type === 'vip' && <div style={fld}><label style={lbl}>Security Description</label><textarea style={{ ...inp, height: '70px' }} value={form.security_description} onChange={e => setForm({ ...form, security_description: e.target.value })} /></div>}
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...fld, flex: 1 }}>
              <label style={lbl}>Disbursed From Account *</label>
              <select style={inp} value={form.mode_of_payment} onChange={e => setForm({ ...form, mode_of_payment: e.target.value })}>
                <option value=''>-- Select Account --</option>
                <option value='cash'>Cash</option>
                <option value='centenary'>Centenary Bank</option>
                <option value='dfcu'>DFCU Bank</option>
              </select>
              <span style={{ fontSize: '11px', color: '#e74c3c' }}>Treasury will be debited on disburse</span>
            </div>
            <div style={{ ...fld, flex: 1 }}>
              <label style={lbl}>Disbursement Reference</label>
              <input style={inp} value={form.disbursement_ref} onChange={e => setForm({ ...form, disbursement_ref: e.target.value })} placeholder="e.g. MTN-2026-XXXX or cheque no." />
            </div>
          </div>
          {!readOnly && <button style={btn()} onClick={submitLoan}>Disburse Loan</button>}
        </div>
      )}

      {view === 'payment' && selectedLoan && (
        <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '24px', maxWidth: '500px' }}>
          <h3 style={{ marginTop: 0, color: '#2c3e50' }}>Record Payment — {selectedLoan.loan_number}</h3>
          <div style={{ background: '#f8f9fa', borderRadius: '6px', padding: '12px', marginBottom: '16px', fontSize: '13px' }}>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <span>Principal: <strong style={{ color: '#1a5276' }}>{fmt(selectedLoan.balance)}</strong></span>
              <span>Interest: <strong style={{ color: '#b7770d' }}>{fmt(selectedLoan.interest_balance)}</strong></span>
              <span>Total: <strong style={{ color: '#6e2fa0' }}>{fmt(parseFloat(selectedLoan.balance) + parseFloat(selectedLoan.interest_balance))}</strong></span>
            </div>
          </div>
          <div style={fld}><label style={lbl}>Amount Paid (UGX) *</label><input type='number' style={inp} value={payForm.total_paid} onChange={e => setPayForm({ ...payForm, total_paid: e.target.value })} /></div>
          <div style={fld}><label style={lbl}>Payment Date *</label><input type='date' style={inp} value={payForm.payment_date} onChange={e => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
          <div style={fld}><label style={lbl}>Receipt Reference</label><input style={inp} value={payForm.receipt_ref} onChange={e => setPayForm({ ...payForm, receipt_ref: e.target.value })} /></div>
          <div style={fld}><label style={lbl}>Mode of Payment</label>
            <select style={inp} value={payForm.mode_of_payment || ''} onChange={e => setPayForm({ ...payForm, mode_of_payment: e.target.value })}>
              <option value=''>-- Mode of Payment --</option>
              <option value='cash'>Cash</option><option value='centenary'>Centenary Bank</option><option value='dfcu'>DFCU Bank</option>
            </select>
          </div>
          <div style={fld}><label style={lbl}>Notes</label><input style={inp} value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })} /></div>
          {payForm.total_paid && parseFloat(payForm.total_paid) >= (parseFloat(selectedLoan.balance) + parseFloat(selectedLoan.interest_balance)) && (
            <div style={{ background: '#d5f5e3', borderRadius: '6px', padding: '10px', marginBottom: '14px', fontSize: '13px', color: '#1e8449', fontWeight: '600' }}>✓ This payment will fully settle the loan</div>
          )}
          {!readOnly && <button style={btn('#1e8449')} onClick={submitPayment}>Record Payment</button>}
        </div>
      )}

      {view === 'detail' && loanDetail && (
        <div>
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '16px' }}>
            <h3 style={{ marginTop: 0, color: '#2c3e50' }}>{loanDetail.loan.loan_number} — {loanDetail.loan.member_name}</h3>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {[['Principal', loanDetail.loan.principal, '#1a5276'], ['Admin Fee', loanDetail.loan.admin_fee_amount, '#888'], ['Net Disbursed', loanDetail.loan.net_disbursed, '#1e8449'], ['Principal Balance', loanDetail.loan.balance, '#1a5276'], ['Interest Accrued', loanDetail.loan.interest_accrued, '#b7770d'], ['Interest Balance', loanDetail.loan.interest_balance, '#b7770d'], ['Principal Paid', loanDetail.loan.principal_paid, '#1e8449'], ['Interest Paid', loanDetail.loan.interest_paid, '#1e8449']].map(([label, val, color]) => (
                <div key={label} style={{ padding: '10px 16px', background: '#f8f9fa', borderRadius: '6px', minWidth: '130px' }}>
                  <div style={{ fontSize: '11px', color: '#888' }}>{label}</div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color }}>{fmt(val)}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <span>📄 Generated: <strong>{fmtTS(loanDetail.loan.created_at)}</strong></span>
              <span>✅ Agreement: <strong>{loanDetail.loan.agreement_accepted_at ? fmtTS(loanDetail.loan.agreement_accepted_at) : 'Pending'}</strong></span>
              <span>💳 Disb. Ref: <strong>{loanDetail.loan.disbursement_ref || 'Pending'}</strong></span>
              <span>📦 Receipt: <strong>{loanDetail.loan.receipt_confirmed_at ? fmtTS(loanDetail.loan.receipt_confirmed_at) : 'Pending'}</strong></span>
            </div>
            <button onClick={() => generateLoanAgreement(loanDetail.loan)} style={{ background: '#1a5276', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>📄 Download Loan Agreement</button>
          </div>

          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, color: '#2c3e50' }}>Interest Postings</h4>
              {userRole === 'admin' && <span style={{ fontSize: '11px', color: '#95a5a6' }}>Historical record — Edit: all roles | Reverse: admin only — unpaid only</span>}
            </div>
            {loanDetail.interest_postings.length === 0
              ? <div style={{ color: '#aaa', fontSize: '13px' }}>No interest posted yet</div>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      {['Month', 'Date', 'Opening Balance', 'Rate', 'Interest Amount', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '8px', textAlign: 'left', color: '#666', fontWeight: '600' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loanDetail.interest_postings.map((p, i) => {
                      if (editingPostingId === p.id) return (
                        <tr key={p.id} style={{ background: '#fffde7', borderBottom: '1px solid #f0f0f0', borderLeft: '4px solid #b7770d' }}>
                          <td style={{ padding: '8px' }}>{p.month_number}</td>
                          <td style={{ padding: '8px' }}>{new Date(p.posting_date).toLocaleDateString('en-GB')}</td>
                          <td style={{ padding: '8px' }}>{fmt(p.opening_balance)}</td>
                          <td style={{ padding: '8px' }}>{p.rate}%</td>
                          <td style={{ padding: '8px' }}>
                            <input type="number"
                              style={{ padding: '4px', border: '1px solid #b7770d', borderRadius: '4px', fontSize: '12px', background: '#fffde7', width: '120px' }}
                              value={editPostingAmount}
                              onChange={e => setEditPostingAmount(e.target.value)}
                              placeholder={p.interest_amount} />
                          </td>
                          <td style={{ padding: '8px' }}>
                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', background: '#fef9e7', color: '#b7770d' }}>{p.status}</span>
                          </td>
                          <td style={{ padding: '8px' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {!readOnly && <button onClick={() => saveEditPosting(p.id)}
                                style={{ background: '#2ecc71', color: 'white', border: 'none', padding: '3px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                                Save
                              </button>}
                              <button onClick={() => setEditingPostingId(null)}
                                style={{ background: '#95a5a6', color: 'white', border: 'none', padding: '3px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      );

                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px' }}>{p.month_number}</td>
                          <td style={{ padding: '8px' }}>{new Date(p.posting_date).toLocaleDateString('en-GB')}</td>
                          <td style={{ padding: '8px' }}>{fmt(p.opening_balance)}</td>
                          <td style={{ padding: '8px' }}>{p.rate}%</td>
                          <td style={{ padding: '8px', fontWeight: '600', color: '#b7770d' }}>{fmt(p.interest_amount)}</td>
                          <td style={{ padding: '8px' }}>
                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', background: p.status === 'paid' ? '#d5f5e3' : '#fef9e7', color: p.status === 'paid' ? '#1e8449' : '#b7770d' }}>{p.status}</span>
                          </td>
                          <td style={{ padding: '8px' }}>
                            {!readOnly && p.status !== 'paid' ? (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button onClick={() => { setEditingPostingId(p.id); setEditPostingAmount(p.interest_amount); }}
                                  style={{ background: '#e67e22', color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                  Edit
                                </button>
                                {userRole === 'admin' && (
                                  <button onClick={() => reversePosting(p.id, p.interest_amount)}
                                    style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                    Reverse
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span style={{ fontSize: '11px', color: '#95a5a6' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            }
          </div>

          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, color: '#2c3e50' }}>Payment History</h4>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {userRole === 'admin' && <span style={{ fontSize: '11px', color: '#95a5a6' }}>Edit: all roles | Delete: admin only</span>}
                <button onClick={() => generateLoanStatement(loanDetail.loan, loanDetail.payments, loanDetail.interest_postings)}
                  style={{ background: '#1a5276', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                  📄 Download Statement
                </button>
              </div>
            </div>
            {loanDetail.payments.length === 0
              ? <div style={{ color: '#aaa', fontSize: '13px' }}>No payments recorded yet</div>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      {['Date', 'Total Paid', 'Principal', 'Interest', 'Principal Bal After', 'Interest Bal After', 'Note', '📄', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '8px', textAlign: 'left', color: '#666', fontWeight: '600' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loanDetail.payments.map((p, i) => {
                      if (editingPaymentId === p.id) return (
                        <tr key={p.id} style={{ background: '#fffde7', borderBottom: '1px solid #f0f0f0', borderLeft: '4px solid #f39c12' }}>
                          <td style={{ padding: '8px' }}>
                            <input type="date" style={{ padding: '4px', border: '1px solid #f39c12', borderRadius: '4px', fontSize: '12px', background: '#fffde7' }}
                              value={editPayData.payment_date || p.payment_date?.slice(0, 10)}
                              onChange={e => setEditPayData({ ...editPayData, payment_date: e.target.value })} />
                          </td>
                          <td style={{ padding: '8px' }}>
                            <input type="number" style={{ padding: '4px', border: '1px solid #f39c12', borderRadius: '4px', fontSize: '12px', background: '#fffde7', width: '110px' }}
                              value={editPayData.total_paid}
                              onChange={e => setEditPayData({ ...editPayData, total_paid: e.target.value })} />
                          </td>
                          <td colSpan={4} style={{ padding: '8px', color: '#999', fontSize: '11px', fontStyle: 'italic' }}>recalculated on save</td>
                          <td style={{ padding: '8px' }}>
                            <input style={{ padding: '4px', border: '1px solid #f39c12', borderRadius: '4px', fontSize: '12px', background: '#fffde7', width: '120px' }}
                              placeholder="Notes"
                              value={editPayData.notes || ''}
                              onChange={e => setEditPayData({ ...editPayData, notes: e.target.value })} />
                          </td>
                          <td style={{ padding: '8px' }}>—</td>
                          <td style={{ padding: '8px' }}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {!readOnly && <button onClick={() => saveEditPayment(p.id)} style={{ background: '#2ecc71', color: 'white', border: 'none', padding: '3px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>Save</button>}
                              <button onClick={() => setEditingPaymentId(null)} style={{ background: '#95a5a6', color: 'white', border: 'none', padding: '3px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      );

                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0', background: p.is_early_settlement ? '#d5f5e3' : 'white' }}>
                          <td style={{ padding: '8px' }}>{new Date(p.payment_date).toLocaleDateString('en-GB')}</td>
                          <td style={{ padding: '8px', fontWeight: '700', color: '#1e8449' }}>{fmt(p.total_paid)}</td>
                          <td style={{ padding: '8px', color: '#1a5276' }}>{fmt(p.principal_portion)}</td>
                          <td style={{ padding: '8px', color: '#b7770d' }}>{fmt(p.interest_portion)}</td>
                          <td style={{ padding: '8px' }}>{fmt(p.principal_balance_after)}</td>
                          <td style={{ padding: '8px' }}>{fmt(p.interest_balance_after)}</td>
                          <td style={{ padding: '8px', fontSize: '11px', color: '#666' }}>{p.is_early_settlement ? '⭐ Early Settlement' : (p.notes || '')}</td>
                          <td style={{ padding: '8px' }}>
                            <button onClick={() => generatePaymentReceipt(p, loanDetail.loan)} style={{ background: '#1e8449', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>📄 Receipt</button>
                          </td>
                          <td style={{ padding: '8px' }}>
                            {!readOnly && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => { setEditingPaymentId(p.id); setEditPayData({ total_paid: p.total_paid, payment_date: p.payment_date?.slice(0, 10), receipt_ref: p.receipt_ref || '', notes: p.notes || '' }); }}
                                style={{ background: '#e67e22', color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                Edit
                              </button>
                              {userRole === 'admin' && (
                                <button onClick={() => deletePayment(p.id, p.total_paid)}
                                  style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                  Delete
                                </button>
                              )}
                            </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            }
          </div>
        </div>
      )}

      {view === 'schedule' && schedule && (
        <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
          {(() => {
            const isLumpSum = schedule.is_lump_sum || schedule.repayment_method === 'flat';
            const row = isLumpSum ? (schedule.schedule[0] || {}) : null;
            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#2c3e50' }}>{isLumpSum ? 'Repayment Statement' : 'Repayment Schedule'} — {schedule.loan_number}</h3>
                    <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 0' }}>
                      {isLumpSum ? 'Flat rate — single lump-sum payment, no monthly instalments.' : 'Reducing balance.'} Early settlement stops all future interest.
                    </p>
                  </div>
                  <button style={btn('#c0392b')} onClick={printSchedule}>🖨 Print PDF</button>
                </div>

                {isLumpSum ? (
                  <div style={{ background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '8px', padding: '24px' }}>
                    <div style={{ fontWeight: '700', color: '#1a5276', fontSize: '15px', marginBottom: '16px' }}>
                      SINGLE LUMP-SUM REPAYMENT — NO MONTHLY INSTALMENTS
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '16px' }}>
                      <div style={{ minWidth: '160px' }}>
                        <div style={{ fontSize: '11px', color: '#666' }}>Principal</div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a5276' }}>{fmt(row.principal_due)}</div>
                      </div>
                      <div style={{ minWidth: '160px' }}>
                        <div style={{ fontSize: '11px', color: '#666' }}>Due Date</div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>{row.due_date}</div>
                      </div>
                    </div>

                    {(parseFloat(schedule.principal_paid || 0) > 0 || parseFloat(schedule.interest_paid || 0) > 0) && (
                      <div style={{ background: '#d5f5e3', border: '1px solid #a9dfbf', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#1e8449', fontWeight: '700', marginBottom: '10px' }}>PAYMENTS ALREADY MADE</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                          <div style={{ minWidth: '150px' }}>
                            <div style={{ fontSize: '11px', color: '#666' }}>Principal Paid</div>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e8449' }}>{fmt(schedule.principal_paid)}</div>
                          </div>
                          <div style={{ minWidth: '150px' }}>
                            <div style={{ fontSize: '11px', color: '#666' }}>Interest Paid</div>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e8449' }}>{fmt(schedule.interest_paid)}</div>
                          </div>
                          <div style={{ minWidth: '150px' }}>
                            <div style={{ fontSize: '11px', color: '#666' }}>Total Paid</div>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e8449' }}>{fmt(parseFloat(schedule.principal_paid || 0) + parseFloat(schedule.interest_paid || 0))}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div style={{ background: '#fff', border: '2px solid #c0392b', borderRadius: '8px', padding: '16px' }}>
                      <div style={{ fontSize: '12px', color: '#666', fontWeight: '700', marginBottom: '10px' }}>CURRENTLY OWED (as of today)</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '10px' }}>
                        <div style={{ minWidth: '150px' }}>
                          <div style={{ fontSize: '11px', color: '#666' }}>Remaining Principal</div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#c0392b' }}>{fmt(Math.max(0, parseFloat(row.principal_due || 0) - parseFloat(schedule.principal_paid || 0)))}</div>
                        </div>
                        <div style={{ minWidth: '150px' }}>
                          <div style={{ fontSize: '11px', color: '#666' }}>Remaining Interest</div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: '#c0392b' }}>{fmt(Math.max(0, parseFloat(row.interest_due || 0) - parseFloat(schedule.interest_paid || 0)))}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Total Remaining Balance</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#c0392b' }}>
                        {fmt(Math.max(0, parseFloat(row.total_due || 0) - parseFloat(schedule.principal_paid || 0) - parseFloat(schedule.interest_paid || 0)))}
                      </div>
                    </div>

                    <p style={{ fontSize: '12px', color: '#555', fontStyle: 'italic', marginTop: '16px', marginBottom: 0 }}>
                      This is a flat-rate loan — one lump-sum payment covers the full remaining balance, due {row.due_date}. Interest is recalculated on the reduced principal after each payment, so remaining interest updates as payments are made rather than staying fixed at the original disbursement figure.
                    </p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead><tr style={{ background: '#2c3e50', color: 'white' }}>{['Month', 'Due Date', 'Opening Balance', 'Principal', 'Interest', 'Total Due', 'Closing Balance'].map(h => <th key={h} style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {schedule.schedule.map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#f8f9fa' }}>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{r.month}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{r.due_date}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(r.opening_balance)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', color: '#1a5276', fontWeight: '600' }}>{fmt(r.principal_due)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', color: '#b7770d', fontWeight: '600' }}>{fmt(r.interest_due)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: '700' }}>{fmt(r.total_due)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', color: '#666' }}>{fmt(r.closing_balance)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: '#f0f4f8', fontWeight: '700' }}>
                        <td colSpan={3} style={{ padding: '10px', textAlign: 'right', color: '#2c3e50' }}>TOTALS</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#1a5276' }}>{fmt(schedule.schedule.reduce((s, r) => s + r.principal_due, 0))}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#b7770d' }}>{fmt(schedule.schedule.reduce((s, r) => s + r.interest_due, 0))}</td>
                        <td style={{ padding: '10px', textAlign: 'right' }}>{fmt(schedule.schedule.reduce((s, r) => s + r.total_due, 0))}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default Loans;